import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import { DAYS_OF_WEEK, REPORT_FREQUENCY } from 'constants/customReportFields';
import useApi from 'helpers/api';
import { formatDate, formatDateTimeString } from 'helpers/utils';
import { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomReportService from 'services/CustomReport.service';
import CustomReportLayout from './CustomReportLayout';

const CustomReportDetails = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [ticketOrigins, setTicketOrigins] = useState([]);

  useEffect(() => {
    loadReport();
    loadTicketOrigins();
  }, [uuid]);

  const loadReport = async () => {
    setIsLoading(true);
    try {
      const response = await CustomReportService.findByUuid(uuid);
      if (response && !response.ok) {
        setReport(response);
      } else {
        toast.error('Erro ao carregar detalhes do relatório');
        navigate('/reports/custom');
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes do relatório');
      navigate('/reports/custom');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTicketOrigins = async () => {
    try {
      const response = await api.ticketOrigins.list();
      if (response && !response.ok) {
        setTicketOrigins(response);
      }
    } catch (error) {
      console.error('Erro ao carregar canais de origem:', error);
    }
  };

  const handleExecute = async () => {
    setIsExecuting(true);
    try {
      const response = await CustomReportService.execute(uuid);
      if (response && !response.ok) {
        toast.success('Relatório executado com sucesso');
        navigate(`/reports/custom/${uuid}/view`);
      } else {
        toast.error('Erro ao executar relatório');
      }
    } catch (error) {
      toast.error('Erro ao executar relatório');
    } finally {
      setIsExecuting(false);
    }
  };

  const getFrequencyLabel = value => {
    const freq = REPORT_FREQUENCY.find(f => f.value === value);
    return freq ? freq.label : value;
  };

  const getDaysLabel = scheduleDetails => {
    if (!scheduleDetails) return '';
    const dayValues = scheduleDetails.split(',').filter(Boolean);
    return dayValues
      .map(val => {
        const day = DAYS_OF_WEEK.find(d => d.value === val);
        return day ? day.label : val;
      })
      .join(', ');
  };

  const isDynamicDateValue = value => {
    if (!value) return false;

    const lowerValue = value.toLowerCase().trim();

    // Valores dinâmicos em português
    const dynamicValues = [
      'hoje',
      'ontem',
      'amanhã',
      'esta semana',
      'semana passada',
      'próxima semana',
      'este mês',
      'mês passado',
      'próximo mês',
      'este ano',
      'ano passado',
      'próximo ano',
      'últimos 7 dias',
      'últimos 30 dias',
      'últimos 90 dias',
      'próximos 7 dias',
      'próximos 30 dias',
      'próximos 90 dias'
    ];

    // Verifica se o valor contém algum dos valores dinâmicos
    return dynamicValues.some(dynamic => lowerValue.includes(dynamic));
  };

  const isISODateString = value => {
    if (!value) return false;

    // Regex para detectar formato ISO 8601
    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    return isoRegex.test(value.trim());
  };

  const formatFilterValue = filter => {
    const fieldId = filter.fieldId?.toLowerCase() || '';
    const displayValue = filter.displayValue;

    if (
      !displayValue &&
      filter.operator !== 'IS_NULL' &&
      filter.operator !== 'IS_NOT_NULL'
    )
      return '-';

    // Formatação para campos de data
    if (
      fieldId.includes('ticket_created_at') ||
      fieldId.includes('ticket_completed_at') ||
      fieldId.includes('ticket_closed_at') ||
      fieldId.includes('ticket_response_time') ||
      fieldId.includes('ticket_resolution_time') ||
      fieldId.includes('ticket_solution_time')
    ) {
      try {
        // Se é um valor dinâmico, retorna como está
        if (isDynamicDateValue(displayValue.toLowerCase())) {
          return displayValue;
        }

        // Se o operador é BETWEEN, o valor contém duas datas separadas por |
        if (filter.operator === 'BETWEEN' && displayValue.includes('|')) {
          const [startDate, endDate] = displayValue.split('|');

          // Se qualquer uma das datas for dinâmica, retorna o valor original
          if (isDynamicDateValue(startDate) || isDynamicDateValue(endDate)) {
            return displayValue;
          }

          // Se ambas são datas ISO, formata
          if (
            isISODateString(startDate.trim()) &&
            isISODateString(endDate.trim())
          ) {
            const formattedStartDate = formatDate(startDate.trim());
            const formattedEndDate = formatDate(endDate.trim());
            return `${formattedStartDate} a ${formattedEndDate}`;
          }

          // Se não são datas ISO, retorna como está
          return displayValue;
        }

        // Para outros operadores, verifica se é uma data ISO válida
        if (isISODateString(displayValue)) {
          return formatDate(displayValue);
        }

        // Se não é data ISO nem dinâmica, retorna como está
        return displayValue;
      } catch {
        return displayValue;
      }
    }

    // Para outros campos, retorna o valor original
    return displayValue;
  };

  if (isLoading) {
    return (
      <CustomReportLayout>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      </CustomReportLayout>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <CustomReportLayout>
      {/* Header Section */}
      <Flex justifyContent="between" alignItems="center" className="mb-2">
        <Flex alignItems="center">
          <Button
            variant="link"
            className="text-decoration-none text-body me-2 p-0"
            size="sm"
            onClick={() => navigate('/reports/custom')}
            title="Voltar para a listagem"
          >
            <FontAwesomeIcon icon="arrow-left" />
          </Button>
          <Flex>
            <h3 className="m-0">{report.name}</h3>
            {report.description && (
              <p className="text-muted mb-0 small">{report.description}</p>
            )}
          </Flex>
        </Flex>
        <Flex>
          <Button
            variant="primary"
            size="sm"
            onClick={handleExecute}
            disabled={isExecuting || report.inactive}
            className="me-2"
          >
            {isExecuting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Executando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="play" className="me-1" />
                Executar
              </>
            )}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate(`/reports/custom/${uuid}/edit`)}
          >
            <FontAwesomeIcon icon="edit" className="me-1" />
            Editar
          </Button>
        </Flex>
      </Flex>

      {/* Main Content */}
      {/* Card 1: Detalhes do Relatório */}
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Card.Title className="mb-4">Detalhes do Relatório</Card.Title>

              {/* Informações Básicas */}
              <div className="mb-4">
                <h6 className="text-muted mb-3">Informações Básicas</h6>
                <Row>
                  <Col xs={12} sm={6} md={3} className="mb-3">
                    <p className="text-muted mb-1 small">Criado por</p>
                    <h6 className="fw-semibold mb-0">
                      {report.user
                        ? `${report.user.firstName} ${report.user.lastName}`
                        : '-'}
                    </h6>
                  </Col>
                  <Col xs={12} sm={6} md={3} className="mb-3">
                    <p className="text-muted mb-1 small">Data de Criação</p>
                    <h6 className="fw-semibold mb-0">
                      {formatDateTimeString(report.creationDate)}
                    </h6>
                  </Col>
                  <Col xs={12} sm={6} md={3} className="mb-3">
                    <p className="text-muted mb-1 small">Última Atualização</p>
                    <h6 className="fw-semibold mb-0">
                      {formatDateTimeString(report.lastUpdateDate)}
                    </h6>
                  </Col>
                  <Col xs={12} sm={6} md={3} className="mb-3">
                    <p className="text-muted mb-1 small">Status</p>
                    <Badge
                      className="fw-semibold"
                      bg={report.inactive ? 'danger' : 'success'}
                    >
                      {report.inactive ? 'Inativo' : 'Ativo'}
                    </Badge>
                  </Col>
                </Row>
              </div>

              {/* Agendamento */}
              <div>
                <h6 className="text-muted mb-3">Agendamento</h6>
                {report.schedule && !report.schedule.inactive ? (
                  <Row>
                    <Col xs={12} sm={6} md={4} className="mb-3">
                      <p className="text-muted mb-1 small">Frequência</p>
                      <h6 className="fw-semibold mb-0">
                        {getFrequencyLabel(report.schedule.frequency)}
                        {report.schedule.frequency === 'WEEKLY' &&
                          report.schedule.scheduleDetails && (
                            <span className="text-muted">
                              {' '}
                              ({getDaysLabel(report.schedule.scheduleDetails)})
                            </span>
                          )}
                      </h6>
                    </Col>
                    <Col xs={12} sm={6} md={4} className="mb-3">
                      <p className="text-muted mb-1 small">Próxima Execução</p>
                      <h6 className="fw-semibold mb-0">
                        {formatDateTimeString(
                          report.schedule.nextExecutionDate
                        )}
                      </h6>
                    </Col>
                    <Col xs={12} sm={12} md={4} className="mb-3">
                      <p className="text-muted mb-1 small">Destinatários</p>
                      <h6 className="fw-semibold mb-0 text-break">
                        {report.schedule.recipients || '-'}
                      </h6>
                    </Col>
                  </Row>
                ) : (
                  <p className="text-muted mb-0">
                    Não há agendamento configurado
                  </p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Card 2: Colunas Selecionadas */}
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Card.Title className="mb-4">
                Colunas Selecionadas ({report.columns.length})
              </Card.Title>
              <div className="border rounded p-3 bg-100">
                <Row className="g-2">
                  {report.columns
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((col, index) => (
                      <Col xs={12} sm={6} md={4} lg={3} key={col.id}>
                        <div className="d-flex align-items-center p-2 bg-body-emphasis rounded border">
                          <Badge bg="primary" className="me-2">
                            {index + 1}
                          </Badge>
                          <span className="small fw-medium text-truncate">
                            {col.fieldDisplayName}
                          </span>
                        </div>
                      </Col>
                    ))}
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Card 3: Filtros */}
      <Row className="g-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Card.Title className="mb-4">
                Filtros ({report.filters.length})
              </Card.Title>
              {report.filters.length > 0 ? (
                <div className="border rounded p-3 bg-100">
                  <div className="table-responsive">
                    <table className="table table-sm table-borderless mb-0">
                      <thead>
                        <tr className="small text-muted">
                          <th>Condição</th>
                          <th>Campo</th>
                          <th>Operador</th>
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.filters.map((filter, index) => (
                          <tr key={filter.id} className="border-bottom">
                            <td className="py-2">
                              <Badge bg="white" className="small text-dark">
                                {filter.conditionType || 'AND'}
                              </Badge>
                            </td>
                            <td className="py-2">
                              <span className="fw-medium small">
                                {filter.fieldDisplayName}
                              </span>
                            </td>
                            <td className="py-2">
                              <Badge bg="white" className="small text-dark">
                                {filter.operatorDisplayName}
                              </Badge>
                            </td>
                            <td className="py-2">
                              <span className="fw-medium small text-break">
                                {formatFilterValue(filter)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-muted mb-0">Não há filtros configurados</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Opções Adicionais - Card separado se existir */}
      {(report.includeTicketNotes || report.includeTicketAppointments) && (
        <Row className="mt-3">
          <Col xs={12}>
            <Card className="shadow-sm">
              <Card.Body className="p-4">
                <Card.Title className="mb-4">Opções Adicionais</Card.Title>
                <div className="border rounded p-3 bg-light">
                  <ul className="list-unstyled mb-0">
                    {report.includeTicketNotes && (
                      <li className="mb-2">
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success me-2"
                        />
                        <span className="small">Incluir Notas de Ticket</span>
                      </li>
                    )}
                    {report.includeTicketAppointments && (
                      <li className="mb-0">
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success me-2"
                        />
                        <span className="small">
                          Incluir Apontamentos de Ticket
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </CustomReportLayout>
  );
};

export default CustomReportDetails;
